import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  Optional,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule, MatChipSelectionChange } from '@angular/material/chips';
import { TranslateModule } from '@ngx-translate/core';
import { DictationCopilotMeta, Note } from '../../../_interfaces/dictation';
import { TranscriptUtterance } from '../../../_helpers/transcription';
import { TranscriptListComponent } from '../../../shared/components/transcript-list/transcript-list.component';
import type { CollapsibleBoxClasses } from '../../../shared/components/collapsible-box/collapsible-box.component';
import { CopilotDetailsSectionsComponent } from '../../../shared/components/copilot-details-sections/copilot-details-sections.component';
import { DetailsFieldsVariant } from '../../../shared/components/details-fields/details-fields.component';
import {
  CollapsibleListClasses,
  CollapsibleListComponent,
} from '../../../shared/components/collapsible-list/collapsible-list.component';
import {
  BottomSheetComponent,
  BottomSheetState,
} from '../../../shared/components/bottom-sheet/bottom-sheet.component';
import { ScrollToBottomDirective } from '../../../shared/directives/scroll-to-bottom.directive';

import { AnalyticsService } from '../../../_services/analytics.service';

export type ContentPanelView = 'transcript' | 'context';

export const CONTENT_PANEL_COLLAPSED_HEIGHT_PX = 72;

@Component({
  selector: 'app-encounter-content-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    TranslateModule,
    TranscriptListComponent,
    CopilotDetailsSectionsComponent,
    CollapsibleListComponent,
    BottomSheetComponent,
    ScrollToBottomDirective,
  ],
  templateUrl: './content-panel.component.html',
  styleUrl: './content-panel.component.scss',
  host: {
    '[class]': 'hostClasses',
  },
})
export class ContentPanelComponent implements OnChanges, AfterViewInit {
  @Input() utterances: TranscriptUtterance[] = [];
  @Input() notes: Note[] = [];
  @Input({ required: true }) copilot!: DictationCopilotMeta;
  @Input() transcriptEnabled = false;
  @Input() uiState: 'initial' | 'recording' | 'paused' = 'initial';
  @Input() showClose = false;
  @Input() selectedTabIndex = 0;
  @Input() isMobile = false;
  @Input() isLegalOrg = false;

  @Output() readonly saveNote = new EventEmitter<string>();
  @Output() readonly deleteNote = new EventEmitter<number>();
  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly selectedTabIndexChange = new EventEmitter<number>();

  @ViewChild(BottomSheetComponent) private bottomSheet?: BottomSheetComponent;
  @ViewChild('contextInput') private readonly contextInput?: ElementRef<HTMLTextAreaElement>;

  selectedMobileView: 'context' | 'transcript' = 'context';
  sheetState: BottomSheetState = 'collapsed';
  readonly collapsedHeightPx = CONTENT_PANEL_COLLAPSED_HEIGHT_PX;
  noteDraft = '';

  constructor(
    @Optional() private analytics: AnalyticsService | null = null,
  ) {}

  get hostClasses(): string {
    if (this.isMobile) {
      return 'contents is-mobile';
    }

    // Initial and paused share the wash so the tab header composites over it.
    // Recording keeps a transparent host; its tab body carries bg-black/16.
    const surface = this.uiState === 'recording' ? 'bg-transparent' : 'bg-black/16';
    const close = this.showClose ? 'show-close' : '';
    return [
      'relative min-h-0 h-full flex flex-col overflow-hidden self-stretch w-full',
      surface,
      `is-${this.uiState === 'recording' ? 'recording' : 'initial'}`,
      close,
    ].filter(Boolean).join(' ');
  }

  get desktopComposerClass(): string {
    return this.desktopSurfaceClasses.composer;
  }

  /** Desktop card, composer, and note-row fills for the active consult state. */
  private get desktopSurfaceClasses(): {
    composer: string;
    panel: string;
    item?: string;
    itemAlt: string;
  } {
    if (this.uiState === 'recording') {
      return {
        composer: 'border border-white/20 bg-white/8 p-4',
        panel: 'rounded-[7px] border border-white/20 bg-white/8',
        itemAlt: 'bg-surface-tint/50',
      };
    }

    return {
      composer: 'border border-white/20 bg-[#1d1b20]/8 p-4',
      panel: 'rounded-[7px] border border-white/20 bg-[#1d1b20]/8',
      item: 'bg-on-primary-container/50',
      itemAlt: 'bg-transparent',
    };
  }

  /** Light mobile boxes use document field colours; desktop glass panels use on-primary text. */
  get detailsFieldsVariant(): DetailsFieldsVariant {
    return this.isMobile ? 'document' : 'encounter';
  }

  get detailsBoxClasses(): CollapsibleBoxClasses {
    if (this.isMobile) {
      return {
        title: 'body-medium !font-normal text-on-surface-variant',
      };
    }

    const surface = this.desktopSurfaceClasses;
    return {
      panel: surface.panel,
      title: 'content-panel-body',
      chevron: '!text-white/50',
    };
  }

  get detailsPlaceholderClasses(): string {
    if (this.isMobile) {
      return 'body-medium !font-normal text-outline';
    }

    return 'content-panel-body content-panel-body--muted';
  }

  get detailsRowClasses(): string {
    if (this.isMobile) {
      return 'body-medium !font-normal';
    }

    return 'content-panel-body';
  }

  get addedContextClasses(): CollapsibleListClasses {
    if (this.isMobile) {
      return {
        title: 'body-medium !font-normal text-on-surface-variant',
        itemText: 'body-medium whitespace-pre-wrap',
        itemAlt: 'bg-surface-container-lowest',
        empty: 'body-medium !font-normal text-outline',
      };
    }

    const surface = this.desktopSurfaceClasses;
    return {
      panel: surface.panel,
      title: 'content-panel-body',
      chevron: '!text-white/50',
      itemText: 'content-panel-body whitespace-pre-wrap',
      ...(surface.item ? { item: surface.item } : {}),
      itemAlt: surface.itemAlt,
      empty: 'content-panel-body content-panel-body--muted',
      remove: '!text-white/50',
    };
  }

  get contextItems(): string[] {
    return this.notes.map((note) => note.note);
  }

  get isExpanded(): boolean {
    return this.sheetState === 'expanded';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isMobile'] && !this.isMobile) {
      this.sheetState = 'collapsed';
    }

    if (changes['selectedTabIndex']) {
      this.selectedMobileView = this.selectedTabIndex === 0 ? 'transcript' : 'context';
      if (this.isMobile && !changes['selectedTabIndex'].firstChange) {
        this.bottomSheet?.expand();
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.isMobile) return;
    this.focusContextInput();
  }

  focusContextInput(): void {
    this.contextInput?.nativeElement.focus();
  }

  onTabChange(index: number): void {
    if (index === this.selectedTabIndex) return;

    this.selectedTabIndex = index;
    this.selectedTabIndexChange.emit(index);
    this.trackContentViewed(index === 0 ? 'transcript' : 'context');
  }

  onChipsChange(view: ContentPanelView | null | undefined): void {
    if (!view) return;
    if (view === 'transcript' && !this.transcriptEnabled) return;
    if (view !== this.selectedMobileView) {
      this.trackContentViewed(view);
    }

    const collapsed = !this.isExpanded;
    this.selectedMobileView = view;
    this.selectedTabIndex = view === 'transcript' ? 0 : 1;
    this.selectedTabIndexChange.emit(this.selectedTabIndex);

    if (collapsed) {
      this.bottomSheet?.expand();
    }
  }

  onSheetStateChange(state: BottomSheetState): void {
    this.sheetState = state;
  }

  keepSelected(event: MatChipSelectionChange): void {
    if (event.isUserInput && !event.selected) {
      event.source.select();
    }
  }

  onSave(): void {
    const trimmed = this.noteDraft.trim();
    if (!trimmed) return;
    this.saveNote.emit(trimmed);
    this.noteDraft = '';
  }

  onNoteKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSave();
    }
  }

  onRemoveFromList(index: number): void {
    if (index < 0 || index >= this.notes.length) return;
    this.deleteNote.emit(index);
  }

  private trackContentViewed(view: ContentPanelView): void {
    this.analytics?.track(
      view === 'context' ? 'context_viewed' : 'transcript_viewed',
      { surface: 'consultation_screen' },
    );
  }
}
